/**
 * DocumentsBoard.tsx — Admin > Documents' two panes, as legacy's Documents
 * section lays them out (html/lib/admin/admin-documents.php): the document
 * library on the left (categories, each holding its documents) and the packets
 * built from it on the right (each holding its items). One column when the
 * page is narrow (legacy collapses at 810px; here by the page's own width).
 *
 * One drag-and-drop context spans both panes, which is why this is not a
 * CategoryBoard (that board owns its own context). It looks like one: the
 * same row colours, grips, drop highlight and floating row.
 *   - A document drags onto another category, to be filed there, or onto a
 *     packet (its header or its open list), to be added to it. The document
 *     stays in the library.
 *   - A packet item drags to a new place in its packet, or into another
 *     packet. Items keep their order: it is the order they are sent in.
 *   - Categories, documents and packets are listed A–Z and never dragged.
 * The page owns the data and saves each drop (documentsBoardState.ts has the
 * rules); this component owns the layout and the drag mechanics.
 *
 * Categories and packets start collapsed, as legacy's do; a click anywhere on
 * the header opens it (an empty one has nothing to open). A collapsed one
 * still takes a drop on its header. A click anywhere on a live document's row
 * opens it, as on every Admin board. Each document shows how many packets use
 * it — "(in 2)" — and a click on that lists them, as legacy's usage badge does.
 *
 * Drag is off on a Locked page, while searching and while deleted rows show
 * (a partial list is never saved); the page's row icons are its own.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { useElementSize } from '@/hooks/useElementSize';
import { cn } from '@/lib/utils';
import type { DocumentPacket, LibraryCategory, LibraryDocument, PacketEntry } from '@/services/documentLibrary.service';
import { ADMIN_CHILD_ROW_CLASS, ADMIN_PARENT_ROW_CLASS } from '../AdminList';
import { ACTIONS_SLOT, DROP_TARGET_CLASS, GRIP_SLOT, innermostUnderPointer } from '../CategoryBoard';
import { packetEntryLabel } from './documentsBoardState';

/** Legacy's breakpoint for putting the packets under the library. */
const TWO_PANE_MIN_WIDTH = 810;

export interface BoardCategoryGroup {
  category: LibraryCategory;
  /** The documents shown under it, A–Z. */
  documents: LibraryDocument[];
}

export interface DocumentsBoardProps {
  /** A–Z, already filtered for search and Show deleted. */
  categories: BoardCategoryGroup[];
  /** A–Z, already filtered; each packet's items in their saved order. */
  packets: DocumentPacket[];
  documentsById: Map<number, LibraryDocument>;
  /** The packets that list each document (deleted ones included). */
  usage: Map<number, DocumentPacket[]>;
  dragDisabled: boolean;
  /** Searching: every listed category and packet is open. */
  expandAll: boolean;
  /** Text under a pane that has nothing to list. */
  emptyLibrary: string;
  emptyPackets: string;
  onMoveDocument: (documentId: number, categoryId: number) => void;
  onAddToPacket: (documentId: number, packetId: number) => void;
  onMoveEntry: (entryId: number, packetId: number, index: number) => void;
  onOpenDocument: (document: LibraryDocument) => void;
  renderCategoryActions: (category: LibraryCategory) => React.ReactNode;
  renderDocumentActions: (document: LibraryDocument) => React.ReactNode;
  renderPacketActions: (packet: DocumentPacket) => React.ReactNode;
  renderEntryActions: (entry: PacketEntry, packet: DocumentPacket) => React.ReactNode;
}

const docDragId = (id: number) => `doc:${id}`;
const entryDragId = (id: number) => `entry:${id}`;
const categoryDropId = (id: number) => `cat:${id}`;
const packetDropId = (id: number) => `pkt:${id}`;
const parseId = (dragId: string | number) => Number(String(dragId).split(':')[1]);

const documentCollisions = innermostUnderPointer((id) => id.startsWith('cat:') || id.startsWith('pkt:'));
const entryCollisions = innermostUnderPointer((id) => id.startsWith('entry:') || id.startsWith('pkt:'));
const collisions: CollisionDetection = (args) =>
  (String(args.active.id).startsWith('doc:') ? documentCollisions : entryCollisions)(args);

const count = (n: number, noun: [string, string]) => `${n} ${n === 1 ? noun[0] : noun[1]}`;

export function DocumentsBoard({
  categories,
  packets,
  documentsById,
  usage,
  dragDisabled,
  expandAll,
  emptyLibrary,
  emptyPackets,
  onMoveDocument,
  onAddToPacket,
  onMoveEntry,
  onOpenDocument,
  renderCategoryActions,
  renderDocumentActions,
  renderPacketActions,
  renderEntryActions,
}: DocumentsBoardProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(rootRef);
  // Before the first measure (width 0), assume the usual wide page.
  const twoPanes = width === 0 || width >= TWO_PANE_MIN_WIDTH;

  const [openCategories, setOpenCategories] = useState<Set<number>>(new Set());
  const [openPackets, setOpenPackets] = useState<Set<number>>(new Set());
  const [usageShown, setUsageShown] = useState<Set<number>>(new Set());
  const [active, setActive] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const packetOfEntry = useMemo(() => {
    const map = new Map<number, DocumentPacket>();
    for (const p of packets) for (const e of p.entries) map.set(e.documentPacketEntryID, p);
    return map;
  }, [packets]);

  const toggle = (setter: React.Dispatch<React.SetStateAction<Set<number>>>, id: number) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  function handleDragStart(event: DragStartEvent) {
    setActive(String(event.active.id));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActive(null);
    if (dragDisabled || !event.over) return;
    const activeId = String(event.active.id);
    const overId = String(event.over.id);

    if (activeId.startsWith('doc:')) {
      const documentId = parseId(activeId);
      if (overId.startsWith('cat:')) onMoveDocument(documentId, parseId(overId));
      else if (overId.startsWith('pkt:')) onAddToPacket(documentId, parseId(overId));
      return;
    }

    const entryId = parseId(activeId);
    const source = packetOfEntry.get(entryId);
    if (!source) return;
    if (overId.startsWith('entry:')) {
      const target = packetOfEntry.get(parseId(overId));
      if (!target) return;
      const index = target.entries.findIndex((e) => e.documentPacketEntryID === parseId(overId));
      onMoveEntry(entryId, target.documentPacketID, index);
    } else if (overId.startsWith('pkt:')) {
      const targetId = parseId(overId);
      // Its own packet's header: nowhere new to go.
      if (targetId !== source.documentPacketID) onMoveEntry(entryId, targetId, Number.MAX_SAFE_INTEGER);
    }
  }

  const draggedDocument = active?.startsWith('doc:') ? documentsById.get(parseId(active)) : undefined;
  const draggedFromCategory = draggedDocument?.categoryID ?? null;

  const library = (
    <section aria-label="Document Library" className={cn('min-w-0', twoPanes && 'min-h-0 overflow-y-auto pr-1')}>
      <h2 className="mb-2 text-sm font-semibold text-slate-700">Document Library</h2>
      <div className="space-y-2" data-testid="documents-library">
        {categories.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">{emptyLibrary}</p>
        ) : (
          categories.map(({ category, documents }) => {
            const open = documents.length > 0 && (expandAll || openCategories.has(category.documentCategoryID));
            return (
              <DropSection
                key={category.documentCategoryID}
                dropId={categoryDropId(category.documentCategoryID)}
                // Only a document lands here (the collision rules); its own category never lights up.
                disabled={dragDisabled || !category.active}
                highlight={draggedFromCategory !== category.documentCategoryID}
                label={category.name}
                testId="document-category"
              >
                {(target) => (
                  <>
                    <GroupHeader
                      title={category.name}
                      countLabel={count(documents.filter((d) => d.active).length, ['document', 'documents'])}
                      deleted={!category.active}
                      expandable={documents.length > 0}
                      expanded={open}
                      target={target}
                      onToggle={() => toggle(setOpenCategories, category.documentCategoryID)}
                      actions={renderCategoryActions(category)}
                    />
                    {open && (
                      <ul className="divide-y divide-slate-100">
                        {documents.map((d) => (
                          <DocumentRow
                            key={d.documentID}
                            document={d}
                            usedIn={usage.get(d.documentID) ?? []}
                            usageShown={usageShown.has(d.documentID)}
                            onToggleUsage={() => toggle(setUsageShown, d.documentID)}
                            dragDisabled={dragDisabled}
                            lifted={active === docDragId(d.documentID)}
                            onOpen={() => onOpenDocument(d)}
                            actions={renderDocumentActions(d)}
                          />
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </DropSection>
            );
          })
        )}
      </div>
    </section>
  );

  const packetPane = (
    <section aria-label="Packets" className={cn('min-w-0', twoPanes && 'min-h-0 overflow-y-auto pr-1')}>
      <h2 className="mb-2 text-sm font-semibold text-slate-700">Packets</h2>
      <div className="space-y-2" data-testid="documents-packets">
        {packets.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-500">{emptyPackets}</p>
        ) : (
          packets.map((packet) => {
            const open = packet.entries.length > 0 && (expandAll || openPackets.has(packet.documentPacketID));
            const draggingOwnEntry = active?.startsWith('entry:') && packetOfEntry.get(parseId(active))?.documentPacketID === packet.documentPacketID;
            return (
              <DropSection
                key={packet.documentPacketID}
                dropId={packetDropId(packet.documentPacketID)}
                disabled={dragDisabled || !packet.active}
                highlight={!draggingOwnEntry}
                label={packet.name}
                testId="document-packet"
              >
                {(target) => (
                  <>
                    <GroupHeader
                      title={packet.name}
                      countLabel={count(packet.entries.length, ['item', 'items'])}
                      deleted={!packet.active}
                      expandable={packet.entries.length > 0}
                      expanded={open}
                      target={target}
                      onToggle={() => toggle(setOpenPackets, packet.documentPacketID)}
                      actions={renderPacketActions(packet)}
                    />
                    {open && (
                      <SortableContext items={packet.entries.map((e) => entryDragId(e.documentPacketEntryID))} strategy={verticalListSortingStrategy}>
                        <ol className="divide-y divide-slate-100">
                          {packet.entries.map((entry) => (
                            <EntryRow
                              key={entry.documentPacketEntryID}
                              entry={entry}
                              label={packetEntryLabel(entry, documentsById)}
                              documentDeleted={entry.documentType === 0 && documentsById.get(entry.documentID)?.active === false}
                              dragDisabled={dragDisabled || !packet.active}
                              actions={renderEntryActions(entry, packet)}
                            />
                          ))}
                        </ol>
                      </SortableContext>
                    )}
                  </>
                )}
              </DropSection>
            );
          })
        )}
      </div>
    </section>
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisions}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActive(null)}
    >
      <div
        ref={rootRef}
        data-testid="documents-board"
        className={cn('grid gap-5', twoPanes ? 'h-full min-h-0 grid-cols-1 md:grid-cols-2' : 'grid-cols-1')}
      >
        {library}
        {packetPane}
      </div>

      {/* A dragged document floats under the pointer; it stays in the library. */}
      <DragOverlay dropAnimation={null}>
        {draggedDocument ? (
          <div className="flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm shadow-lg">
            <span className={GRIP_SLOT}>
              <GripVertical className="h-4 w-4 text-slate-400" />
            </span>
            <span className="truncate font-medium text-slate-700">{draggedDocument.title}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

function DropSection({
  dropId,
  disabled,
  highlight = true,
  label,
  testId,
  children,
}: {
  dropId: string;
  /** Takes no drop (and never lights up). */
  disabled: boolean;
  /** False: takes the drop without lighting up (an item over its own packet). */
  highlight?: boolean;
  label: string;
  testId: string;
  children: (target: boolean) => React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId, disabled });
  const target = isOver && !disabled && highlight;
  return (
    <section
      ref={setNodeRef}
      aria-label={label}
      data-testid={testId}
      className={cn('rounded-lg border border-slate-200 bg-white', target && DROP_TARGET_CLASS)}
    >
      {children(target)}
    </section>
  );
}

function GroupHeader({
  title,
  countLabel,
  deleted,
  expandable,
  expanded,
  target,
  onToggle,
  actions,
}: {
  title: string;
  countLabel: string;
  deleted: boolean;
  /** False: nothing in it, so no chevron and no toggle. */
  expandable: boolean;
  expanded: boolean;
  target: boolean;
  onToggle: () => void;
  actions: React.ReactNode;
}) {
  const name = (
    <span className={cn('truncate font-medium', deleted ? 'text-slate-400 line-through' : 'text-slate-900')}>{title}</span>
  );
  return (
    <header
      // A click anywhere on the header opens or closes it (Patient Notes' parent row).
      onClick={expandable ? onToggle : undefined}
      className={cn(
        'flex items-center gap-2 rounded-lg px-3 py-2',
        expandable ? ADMIN_PARENT_ROW_CLASS : 'bg-slate-100 transition-colors',
        expanded && 'rounded-b-none',
        target && 'bg-indigo-50 hover:bg-indigo-50',
      )}
    >
      {expandable ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
          {name}
          <Badge variant="neutral" size="sm">{countLabel}</Badge>
          {deleted && <Badge variant="error" size="sm">Deleted</Badge>}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="h-4 w-4 shrink-0" aria-hidden="true" />
          {name}
          <Badge variant="neutral" size="sm">{countLabel}</Badge>
          {deleted && <Badge variant="error" size="sm">Deleted</Badge>}
        </div>
      )}
      <div className={ACTIONS_SLOT} onClick={(e) => e.stopPropagation()}>
        {actions}
      </div>
    </header>
  );
}

function Grip({ attributes, listeners, setRef, title }: { attributes: object; listeners: object | undefined; setRef: (el: HTMLElement | null) => void; title: string }) {
  return (
    <button
      type="button"
      ref={setRef}
      {...attributes}
      {...listeners}
      // A press on the grip that never drags is still a click: it must not open the row.
      onClick={(e) => e.stopPropagation()}
      className="cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"
      aria-label="Drag to move"
      title={title}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
}

function DocumentRow({
  document: d,
  usedIn,
  usageShown,
  onToggleUsage,
  dragDisabled,
  lifted,
  onOpen,
  actions,
}: {
  document: LibraryDocument;
  usedIn: DocumentPacket[];
  usageShown: boolean;
  onToggleUsage: () => void;
  dragDisabled: boolean;
  lifted: boolean;
  onOpen: () => void;
  actions: React.ReactNode;
}) {
  const deleted = !d.active;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({
    id: docDragId(d.documentID),
    disabled: dragDisabled || deleted,
  });
  const title = d.title.trim() || `Document #${d.documentID}`;

  return (
    <li ref={setNodeRef} data-testid="library-document" data-document-id={d.documentID} className={cn(lifted && 'opacity-40')}>
      {/* A click anywhere on a live row opens it, as on every Admin board; a deleted one is restored, not edited. */}
      <div
        data-testid="library-document-row"
        onClick={deleted ? undefined : onOpen}
        className={cn('flex items-center gap-2 px-3 text-sm', deleted ? 'bg-slate-50' : ADMIN_CHILD_ROW_CLASS, !deleted && 'cursor-pointer')}
      >
        <span className={GRIP_SLOT}>
          {!dragDisabled && !deleted && (
            <Grip attributes={attributes} listeners={listeners} setRef={setActivatorNodeRef} title="Drag onto a category to file it there, or onto a packet to add it" />
          )}
        </span>
        <div className={cn('flex min-w-0 flex-1 items-center gap-2 py-2', deleted && 'opacity-60')}>
          {/* The row's own click opens it; the title is a button so the keyboard can too. */}
          <button
            type="button"
            disabled={deleted}
            data-testid="document-title"
            className="min-w-0 truncate rounded text-left font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default"
          >
            {title}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleUsage();
            }}
            aria-expanded={usageShown}
            title="Which packets include this document"
            data-testid="document-usage"
            className="shrink-0 rounded text-xs text-slate-500 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            (in {usedIn.length})
          </button>
        </div>
        <div className={cn(ACTIONS_SLOT, 'py-1')} onClick={(e) => e.stopPropagation()}>
          {deleted && <Badge variant="error" size="sm">Deleted</Badge>}
          {actions}
        </div>
      </div>
      {usageShown && (
        <p className="px-9 pb-2 text-xs text-slate-500" data-testid="document-usage-detail">
          {usedIn.length === 0
            ? 'Not used in any packet yet.'
            : `In: ${usedIn.map((p) => (p.active ? p.name : `${p.name} (deleted)`)).join(', ')}`}
        </p>
      )}
    </li>
  );
}

function EntryRow({
  entry,
  label,
  documentDeleted,
  dragDisabled,
  actions,
}: {
  entry: PacketEntry;
  label: string;
  documentDeleted: boolean;
  dragDisabled: boolean;
  actions: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: entryDragId(entry.documentPacketEntryID),
    disabled: dragDisabled,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid="packet-entry"
      className={cn('flex items-center gap-2 px-3 text-sm', ADMIN_CHILD_ROW_CLASS, isDragging && 'relative z-10 shadow-md opacity-90')}
    >
      <span className={GRIP_SLOT}>
        {!dragDisabled && (
          <Grip attributes={attributes} listeners={listeners} setRef={setActivatorNodeRef} title="Drag to change the order, or onto another packet to move it there" />
        )}
      </span>
      <div className="flex min-w-0 flex-1 items-center gap-2 py-2">
        <span className="truncate text-slate-700">{label}</span>
        {documentDeleted && <span className="shrink-0 text-xs text-amber-700">(deleted document)</span>}
      </div>
      <div className={cn(ACTIONS_SLOT, 'py-1')}>{actions}</div>
    </li>
  );
}
